import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
} from "@/components/icons";
import type { TableColumn, SortOption } from "@/types/component";
import type { CompanyTrainingProfile } from "../types/company-training-profiles.types";
import { companyTrainingProfilesApi } from "../services/company-training-profiles.api";
import CompanyTrainingProfileDetailDrawer from "../components/CompanyTrainingProfileDetailDrawer";
import CompanyTrainingProfileFormDrawer from "../components/CompanyTrainingProfileFormDrawer";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./CompanyTrainingProfilesPage.module.scss";

const CompanyTrainingProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.COMPANY_TRAINING_PROFILES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.COMPANY_TRAINING_PROFILES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.COMPANY_TRAINING_PROFILES_DELETE);

  const [profiles, setProfiles] = useState<CompanyTrainingProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [sortBy, setSortBy] = useState<
    "createdAt" | "companyName" | "updatedAt"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [detailProfile, setDetailProfile] =
    useState<CompanyTrainingProfile | null>(null);
  const [editProfile, setEditProfile] =
    useState<CompanyTrainingProfile | null>(null);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [confirmDeleteProfile, setConfirmDeleteProfile] =
    useState<CompanyTrainingProfile | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchProfiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await companyTrainingProfilesApi.getAll({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        sortBy,
        sortOrder,
      });
      const mapped: CompanyTrainingProfile[] = response.data.map((item) => ({
        id: item.id,
        companyName: item.companyName,
        contactPhoneNumber: item.contactPhoneNumber,
        trainingsTaken: item.trainingsTaken ?? [],
        traineeEvaluations: item.traineeEvaluations ?? [],
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));
      setProfiles(mapped);
      setTotalRecords(response.total);
    } catch {
      toast.error(t("companyTrainingProfiles.toast.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, sortBy, sortOrder, t]);

  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleOpenDetail = async (row: CompanyTrainingProfile) => {
    try {
      const full = await companyTrainingProfilesApi.getById(row.id);
      setDetailProfile(full);
    } catch {
      setDetailProfile(row);
    }
  };

  const handleOpenEdit = async (row: CompanyTrainingProfile) => {
    try {
      const full = await companyTrainingProfilesApi.getById(row.id);
      setEditProfile(full);
    } catch {
      setEditProfile(row);
    }
  };

  const handleDelete = (row: CompanyTrainingProfile) => {
    setConfirmDeleteProfile(row);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteProfile) return;
    setIsDeleteLoading(true);
    try {
      await companyTrainingProfilesApi.remove(confirmDeleteProfile.id);
      setConfirmDeleteProfile(null);
      setProfiles((prev) =>
        prev.filter((p) => p.id !== confirmDeleteProfile.id),
      );
      toast.success(t("companyTrainingProfiles.toast.deleteSuccess"));
    } catch {
      toast.error(t("companyTrainingProfiles.toast.deleteError"));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (
    data: {
      companyName: string;
      contactPhoneNumber: string;
      trainingsTaken: string[];
      traineeEvaluations: string[];
    },
    id?: string,
  ) => {
    try {
      if (id) {
        await companyTrainingProfilesApi.update(id, data);
        toast.success(t("companyTrainingProfiles.toast.updateSuccess"));
        setEditProfile(null);
      } else {
        await companyTrainingProfilesApi.create(data);
        toast.success(t("companyTrainingProfiles.toast.createSuccess"));
        setIsAddDrawerOpen(false);
      }
      void fetchProfiles();
    } catch {
      toast.error(t("companyTrainingProfiles.toast.saveError"));
    }
  };

  const sortOptions: SortOption[] = [
    {
      label: t("companyTrainingProfiles.sort.companyNameAsc"),
      value: "companyName_asc",
    },
    {
      label: t("companyTrainingProfiles.sort.companyNameDesc"),
      value: "companyName_desc",
    },
    {
      label: t("companyTrainingProfiles.sort.createdAtNew"),
      value: "createdAt_desc",
    },
    {
      label: t("companyTrainingProfiles.sort.createdAtOld"),
      value: "createdAt_asc",
    },
  ];

  const handleSortChange = (option: SortOption) => {
    const [field, order] = (option.value as string).split("_") as [
      "createdAt" | "companyName" | "updatedAt",
      "asc" | "desc",
    ];
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const columns: TableColumn<CompanyTrainingProfile>[] = [
    {
      header: t("companyTrainingProfiles.columns.companyName"),
      accessor: "companyName",
    },
    {
      header: t("companyTrainingProfiles.columns.contactPhoneNumber"),
      accessor: "contactPhoneNumber",
    },
    {
      header: t("companyTrainingProfiles.columns.trainingsTaken"),
      accessor: (row) => (
        <div className={styles.chipList}>
          {row.trainingsTaken.slice(0, 3).map((item, idx) => (
            <span key={idx} className={styles.chip}>
              {item}
            </span>
          ))}
          {row.trainingsTaken.length > 3 && (
            <span className={styles.chip}>+{row.trainingsTaken.length - 3}</span>
          )}
        </div>
      ),
    },
    {
      header: t("companyTrainingProfiles.columns.traineeEvaluations"),
      accessor: (row) => (
        <div className={styles.chipList}>
          {row.traineeEvaluations.slice(0, 3).map((item, idx) => (
            <span key={idx} className={styles.chip}>
              {item}
            </span>
          ))}
          {row.traineeEvaluations.length > 3 && (
            <span className={styles.chip}>
              +{row.traineeEvaluations.length - 3}
            </span>
          )}
        </div>
      ),
    },
  ];

  const renderActions = (row: CompanyTrainingProfile) => {
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;

    const menuItems = [
      {
        label: t("companyTrainingProfiles.actions.view"),
        icon: <EyeIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          void handleOpenDetail(row);
        },
      },
      canUpdate && {
        label: t("companyTrainingProfiles.actions.edit"),
        icon: <EditIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          void handleOpenEdit(row);
        },
      },
      canDelete && {
        label: t("companyTrainingProfiles.actions.delete"),
        icon: <EditIcon width={18} height={18} />,
        onClick: () => {
          handleDropdownToggle(rowId, false);
          handleDelete(row);
        },
      },
    ].filter(Boolean) as {
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
    }[];

    const menuContent = (
      <div className={styles.actionMenu}>
        {menuItems.map((item, index) => (
          <button
            key={index}
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              item.onClick();
            }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
    );

    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          type="button"
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("companyTrainingProfiles.title")} />

      <PageHeader
        searchPlaceholder={t("companyTrainingProfiles.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        onSort={handleSortChange}
        onAddNew={canCreate ? () => setIsAddDrawerOpen(true) : undefined}
        addButtonLabel={
          canCreate ? t("companyTrainingProfiles.addButton") : undefined
        }
      />

      <Table
        columns={columns}
        data={profiles}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        emptyMessage={t("companyTrainingProfiles.noData")}
      />

      <CompanyTrainingProfileDetailDrawer
        isOpen={!!detailProfile}
        onClose={() => setDetailProfile(null)}
        profile={detailProfile}
      />

      <CompanyTrainingProfileFormDrawer
        isOpen={isAddDrawerOpen || !!editProfile}
        onClose={() => {
          setIsAddDrawerOpen(false);
          setEditProfile(null);
        }}
        mode={editProfile ? "edit" : "add"}
        profile={editProfile}
        onSubmit={handleFormSubmit}
      />

      <ConfirmModal
        isOpen={!!confirmDeleteProfile}
        onClose={() => setConfirmDeleteProfile(null)}
        onConfirm={handleConfirmDelete}
        title={t("companyTrainingProfiles.deleteModal.title")}
        message={
          confirmDeleteProfile
            ? t("companyTrainingProfiles.deleteModal.message", {
                name: confirmDeleteProfile.companyName,
              })
            : ""
        }
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        loading={isDeleteLoading}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default CompanyTrainingProfilesPage;
